class HttpResponse {
  constructor(response, data) {
    this.status = response.status
    this.statusText = response.statusText
    this.ok = response.ok
    this.headers = response.headers
    this.body = data
    this.data = data
    this.json = () => Promise.resolve(data)
    this.text = () => Promise.resolve(typeof data === 'string' ? data : JSON.stringify(data))
  }
}

class Http {
  constructor() {
    this.interceptors = []
  }

  /**
   * ⚠️ `Content-Type` idzie WYŁĄCZNIE z żądaniem, które ma ciało.
   *
   * Nagłówek opisuje ciało - przy GET-cie nie opisuje niczego, a ma realny skutek uboczny:
   * `application/json` NIE jest wartością safelistowaną przez CORS, więc zamienia zwykłe
   * żądanie cross-origin w takie, które wymaga **preflightu OPTIONS**. Fallback REST statusu PIS
   * (`wtn.bm.pl`) rozbijał się dokładnie o to: serwer oddaje `Access-Control-Allow-Origin` na GET,
   * ale na OPTIONS nie oddaje żadnych nagłówków CORS, więc przeglądarka odrzucała żądanie
   * `TypeError`-em, zanim GET w ogóle wyszedł (pomiar 03.09.2026).
   *
   * Dla żądań same-origin to bez znaczenia (CORS ich nie dotyczy), więc zmiana jest bezpieczna
   * dla całej reszty aplikacji - zdejmuje tylko zbędne preflighty z wywołań zewnętrznych.
   *
   * @param {object} [options]
   * @returns {object}
   */
  _buildOptions(options = {}) {
    const defaultHeaders = {
      Accept: 'application/json',
    }
    if (options.body !== undefined) {
      defaultHeaders['Content-Type'] = 'application/json'
    }

    return {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
      credentials: options.credentials || 'same-origin',
    }
  }

  async _processResponse(response) {
    let data
    const contentType = response.headers.get('content-type')

    if (contentType && contentType.includes('application/json')) {
      data = await response.json()
    } else {
      data = await response.text()
    }

    const httpResponse = new HttpResponse(response, data)

    if (!response.ok) {
      const error = new Error(response.statusText)
      error.response = httpResponse
      error.status = response.status
      error.body = data
      throw error
    }

    return httpResponse
  }

  async get(url, options = {}) {
    const fetchOptions = this._buildOptions({
      ...options,
      method: 'GET',
    })

    const response = await fetch(url, fetchOptions)
    return this._processResponse(response)
  }

  async post(url, data = null, options = {}) {
    const fetchOptions = this._buildOptions({
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    })

    const response = await fetch(url, fetchOptions)
    return this._processResponse(response)
  }

  async put(url, data = null, options = {}) {
    const fetchOptions = this._buildOptions({
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    })

    const response = await fetch(url, fetchOptions)
    return this._processResponse(response)
  }

  async delete(url, options = {}) {
    const fetchOptions = this._buildOptions({
      ...options,
      method: 'DELETE',
    })

    const response = await fetch(url, fetchOptions)
    return this._processResponse(response)
  }

  async patch(url, data = null, options = {}) {
    const fetchOptions = this._buildOptions({
      ...options,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    })

    const response = await fetch(url, fetchOptions)
    return this._processResponse(response)
  }

  async head(url, options = {}) {
    const fetchOptions = this._buildOptions({
      ...options,
      method: 'HEAD',
    })

    const response = await fetch(url, fetchOptions)
    return this._processResponse(response)
  }

  async options(url, options = {}) {
    const fetchOptions = this._buildOptions({
      ...options,
      method: 'OPTIONS',
    })

    const response = await fetch(url, fetchOptions)
    return this._processResponse(response)
  }
}

export { Http, HttpResponse }

export const http = new Http()
